import { useEffect, useRef, useState } from 'react';
import { DevicePhoneMobileIcon, ComputerDesktopIcon, ArrowsPointingOutIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface EmailTemplatePreviewProps {
  html: string;
  compact?: boolean;
}

export default function EmailTemplatePreview({ html, compact = false }: EmailTemplatePreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const expandedIframeRef = useRef<HTMLIFrameElement>(null);
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body{margin:0;padding:0;}</style></head><body>${html}</body></html>`);
    doc.close();
  }, [html]);

  useEffect(() => {
    if (!expanded) return;
    const iframe = expandedIframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body{margin:0;padding:0;}</style></head><body>${html}</body></html>`);
    doc.close();
  }, [html, expanded]);

  useEffect(() => {
    if (!expanded) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [expanded]);

  if (compact) {
    return (
      <div className="relative overflow-hidden rounded-lg bg-[#f4f4f4]" style={{ height: 220 }}>
        <iframe
          ref={iframeRef}
          className="absolute top-0 left-0 border-0 pointer-events-none"
          style={{
            width: 600,
            height: 900,
            transform: 'scale(0.365)',
            transformOrigin: 'top left',
          }}
          title="Template Preview"
        />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 rounded-t-xl">
          <span className="text-sm font-medium text-gray-600">Preview</span>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-gray-200 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('desktop')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'desktop' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <ComputerDesktopIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('mobile')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'mobile' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <DevicePhoneMobileIcon className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => setExpanded(true)}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-200 transition-colors"
              title="Expand preview"
            >
              <ArrowsPointingOutIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 p-4 flex justify-center rounded-b-xl">
          <div
            className="transition-all duration-300"
            style={{ width: viewMode === 'desktop' ? '100%' : 375, maxWidth: '100%' }}
          >
            <iframe
              ref={iframeRef}
              className="w-full border-0 bg-white rounded-lg shadow-sm"
              style={{ height: 700 }}
              title="Email Preview"
            />
          </div>
        </div>
      </div>

      {expanded && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="relative w-[95vw] h-[92vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-b border-gray-200">
              <span className="text-sm font-semibold text-gray-700">Email Template Preview</span>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-gray-200 rounded-lg p-0.5">
                  <button
                    onClick={() => setViewMode('desktop')}
                    className={`p-1.5 rounded-md transition-colors ${
                      viewMode === 'desktop' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <ComputerDesktopIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('mobile')}
                    className={`p-1.5 rounded-md transition-colors ${
                      viewMode === 'mobile' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <DevicePhoneMobileIcon className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => setExpanded(false)}
                  className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-200 transition-colors"
                  title="Close (Esc)"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-gray-100 p-6 flex justify-center">
              <div
                className="transition-all duration-300"
                style={{ width: viewMode === 'desktop' ? 660 : 375, maxWidth: '100%' }}
              >
                <iframe
                  ref={expandedIframeRef}
                  className="w-full border-0 bg-white rounded-lg shadow-sm"
                  style={{ height: 'calc(92vh - 120px)' }}
                  title="Email Preview Expanded"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
